import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, Button, useToast, Badge, Table, THead, TBody, TRow, TH, TD, EmptyState, PageHeader, Toolbar, PageLayout, Pagination, Switch, FadeIn, Select } from "@dawa/ui";
import { Skeleton } from "@dawa/ui";
import { supabase } from "../lib/supabase";
import { apiFetch } from "../lib/api";

export default function Contents() {
  const [contents, setContents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const { addToast, Toasts } = useToast();
  const [running, setRunning] = useState<Record<string, boolean>>({});
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [bulkRunning, setBulkRunning] = useState(false);
  const [projects, setProjects] = useState<Array<{ id: string; name: string }>>([]);
  const [projectFilter, setProjectFilter] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<"all"|"complete"|"incomplete">("all");

  useEffect(() => {
    (async () => {
      const s = await supabase.auth.getSession();
      const token = s.data.session?.access_token;
      const res = await apiFetch("/v1/contents", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) addToast("Échec du chargement des contenus", "error");
      const j = await res.json().catch(() => []);
      if (!Array.isArray(j)) {
        addToast("Format inattendu pour la liste des contenus", "error");
        setContents([]);
      } else {
        setContents(j);
      }
      try {
        const pr = await apiFetch("/v1/projects", { headers: { Authorization: `Bearer ${token}` } });
        const pj = await pr.json().catch(() => []);
        if (Array.isArray(pj)) setProjects(pj.map((p: any) => ({ id: p.id, name: p.name })));
      } catch { /* ignore */ }
      setLoading(false);
    })();
  }, []);

  const [incompleteOnly, setIncompleteOnly] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const listBase = q
    ? contents.filter((c) => String(c.primary_keyword || "").toLowerCase().includes(q.toLowerCase()))
    : contents;
  const listProject = projectFilter
    ? listBase.filter((c) => String(c.project_id || "") === projectFilter)
    : listBase;
  const listStatus = statusFilter === "all"
    ? listProject
    : statusFilter === "incomplete"
      ? listProject.filter((c) => !(c.outline_md && c.meta_json && c.article_md && c.score_json))
      : listProject.filter((c) => (c.outline_md && c.meta_json && c.article_md && c.score_json));
  const list = incompleteOnly ? listStatus.filter((c) => !(c.outline_md && c.meta_json && c.article_md && c.score_json)) : listStatus;
  const total = list.length;
  const slice = list.slice((page - 1) * pageSize, page * pageSize);

  return (
    <PageLayout maxWidth="full" className="space-y-4">
      <PageHeader
        title="Mes contenus"
        breadcrumbs={[{ label: "Dashboard", href: "/dashboard" }, { label: "Contenus" }]}
        actions={<Link to="/content/new"><Button>Nouveau contenu</Button></Link>}
      />
      <Toolbar
        searchValue={q}
        onSearchChange={(v) => {
          setQ(v);
          setPage(1);
        }}
        placeholder="Rechercher un mot-clé"
        actions={
          <div className="inline-flex items-center gap-2 text-sm">
            <span className="text-xs text-muted">Sélection: {Object.values(selected).filter(Boolean).length}</span>
            <Button size="sm" variant="ghost" onClick={() => setSelected({})}>Effacer la sélection</Button>
            <div className="mx-2 h-4 w-px bg-muted/40" />
            <Select value={projectFilter} onChange={(e) => { setProjectFilter(e.target.value); setPage(1); }}>
              <option value="">Tous les projets</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </Select>
            <div className="mx-2 h-4 w-px bg-muted/40" />
            <Select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value as any); setPage(1); }}>
              <option value="all">Tous les statuts</option>
              <option value="incomplete">Incomplet</option>
              <option value="complete">Complet</option>
            </Select>
            <div className="mx-2 h-4 w-px bg-muted/40" />
            <Switch checked={incompleteOnly} onCheckedChange={(v) => { setIncompleteOnly(v); setPage(1); }} aria-label="Filtrer incomplets" />
            <span>Incomplets uniquement</span>
            <div className="mx-2 h-4 w-px bg-muted/40" />
            <Button
              size="sm"
              variant="secondary"
              disabled={bulkRunning || Object.keys(selected).filter((id) => selected[id]).length === 0}
              onClick={async () => {
                const toRun = Object.keys(selected).filter((id) => selected[id]);
                if (toRun.length === 0) return;
                setBulkRunning(true);
                const s = await supabase.auth.getSession();
                const token = s.data.session?.access_token;
                if (!token) {
                  setBulkRunning(false);
                  return;
                }
                let ok = 0, conflict = 0, denied = 0, fail = 0;
                for (const id of toRun) {
                  try {
                    const res = await apiFetch(`/v1/generate/batch`, {
                      method: "POST",
                      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                      body: JSON.stringify({ contentId: id }),
                    });
                    if (res.status === 200) ok++;
                    else if (res.status === 409) conflict++;
                    else if (res.status === 402) denied++;
                    else fail++;
                  } catch {
                    fail++;
                  }
                }
                addToast(`Génération: ${ok} ok, ${conflict} en cours, ${denied} sans crédits, ${fail} erreurs`, ok > 0 && fail === 0 && denied === 0 ? "success" : "info");
                setBulkRunning(false);
              }}
            >
              Générer la sélection
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                const next: Record<string, boolean> = { ...selected };
                for (const c of list) next[c.id] = true;
                setSelected(next);
              }}
            >
              Sélectionner tout (filtré)
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                const headers = ["id", "keyword", "created_at", "has_outline", "has_meta", "has_draft", "has_score", "project_id"]; 
                const rows = list.map((c) => [
                  c.id,
                  (c.primary_keyword || "").replace(/\s+/g, " ").trim(),
                  new Date(c.created_at).toISOString(),
                  c.outline_md ? "1" : "0",
                  c.meta_json ? "1" : "0",
                  c.article_md ? "1" : "0",
                  c.score_json ? "1" : "0",
                  c.project_id || "",
                ]);
                const csv = [headers.join(","), ...rows.map((r) => r.map((s) => String(s).includes(',') ? `"${String(s).replace(/"/g, '""')}"` : String(s)).join(","))].join("\n");
                const blob = new Blob([csv], { type: "text/csv" });
                const a = document.createElement("a");
                a.href = URL.createObjectURL(blob);
                a.download = `contents-${Date.now()}.csv`;
                a.click();
              }}
            >
              Exporter CSV
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                const chosen = contents.filter((c) => selected[c.id]);
                if (chosen.length === 0) return;
                const headers = ["id", "keyword", "created_at", "has_outline", "has_meta", "has_draft", "has_score", "project_id"]; 
                const rows = chosen.map((c) => [
                  c.id,
                  (c.primary_keyword || "").replace(/\s+/g, " ").trim(),
                  new Date(c.created_at).toISOString(),
                  c.outline_md ? "1" : "0",
                  c.meta_json ? "1" : "0",
                  c.article_md ? "1" : "0",
                  c.score_json ? "1" : "0",
                  c.project_id || "",
                ]);
                const csv = [headers.join(","), ...rows.map((r) => r.map((s) => String(s).includes(',') ? `"${String(s).replace(/"/g, '""')}"` : String(s)).join(","))].join("\n");
                const blob = new Blob([csv], { type: "text/csv" });
                const a = document.createElement("a");
                a.href = URL.createObjectURL(blob);
                a.download = `contents-selection-${Date.now()}.csv`;
                a.click();
              }}
            >
              Exporter sélection
            </Button>
          </div>
        }
      />
      <FadeIn>
        {loading ? (
          <Card className="p-4">
            <Skeleton lines={3} />
          </Card>
        ) : list.length === 0 ? (
          <EmptyState
            title="Aucun contenu pour le moment"
            description="Créez votre premier contenu pour démarrer vos analyses et générations."
            action={
              <Link to="/content/new">
                <Button>Nouveau contenu</Button>
              </Link>
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <THead>
                <TRow>
                  <TH>
                    <input
                      type="checkbox"
                      aria-label="Tout sélectionner (page)"
                      checked={slice.length > 0 && slice.every((c) => selected[c.id])}
                      onChange={(e) => {
                        const checked = e.currentTarget.checked;
                        const next = { ...selected };
                        for (const c of slice) next[c.id] = checked;
                        setSelected(next);
                      }}
                    />
                  </TH>
                  <TH>Mot-clé</TH>
                  <TH>Date</TH>
                  <TH>États</TH>
                  <TH>Actions</TH>
                </TRow>
              </THead>
              <TBody>
                {slice.map((c) => {
                  const hasOutline = !!c.outline_md;
                  const hasMeta = !!c.meta_json;
                  const hasScore = !!c.score_json;
                  const hasDraft = !!c.article_md;
                  const allDone = hasOutline && hasMeta && hasDraft && hasScore;
                  return (
                    <TRow key={c.id}>
                      <TD>
                        <input
                          type="checkbox"
                          aria-label={`Sélectionner ${c.primary_keyword}`}
                          checked={!!selected[c.id]}
                          onChange={(e) => setSelected((m) => ({ ...m, [c.id]: e.currentTarget.checked }))}
                        />
                      </TD>
                      <TD className="font-semibold">{c.primary_keyword}</TD>
                      <TD className="text-muted">{new Date(c.created_at).toLocaleString()}</TD>
                      <TD>
                        <div className="flex flex-wrap gap-1">
                          <Badge variant={hasOutline ? "success" : "secondary"} className="text-[11px]">Plan</Badge>
                          <Badge variant={hasMeta ? "success" : "secondary"} className="text-[11px]">Meta</Badge>
                          <Badge variant={hasDraft ? "success" : "secondary"} className="text-[11px]">Brouillon</Badge>
                          <Badge variant={hasScore ? "success" : "secondary"} className="text-[11px]">Score</Badge>
                        </div>
                      </TD>
                      <TD className="whitespace-nowrap">
                        {!allDone && (
                          <Button
                            size="sm"
                            variant="subtle"
                            onClick={async () => {
                              const s = await supabase.auth.getSession();
                              const token = s.data.session?.access_token;
                              if (!token) return;
                              setRunning((m) => ({ ...m, [c.id]: true }));
                              const res = await apiFetch(`/v1/generate/batch`, {
                                method: "POST",
                                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                                body: JSON.stringify({ contentId: c.id }),
                              });
                              setRunning((m) => ({ ...m, [c.id]: false }));
                              if (res.status === 200) {
                                addToast("Génération lancée", "success");
                              } else if (res.status === 409) {
                                addToast("Génération déjà en cours", "info");
                              } else if (res.status === 402) {
                                addToast("Crédits insuffisants", "error");
                              } else {
                                const d = await res.json().catch(() => ({} as any));
                                addToast(d?.error ? String(d.error) : `Erreur ${res.status}` , "error");
                              }
                            }}
                            disabled={!!running[c.id]}
                          >
                            {running[c.id] ? "En cours..." : "Générer"}
                          </Button>
                        )}
                        <Link to={`/content/${c.id}`} className="ml-2 text-accent underline-offset-4 hover:underline">
                          Ouvrir
                        </Link>
                      </TD>
                    </TRow>
                  );
                })}
              </TBody>
            </Table>
          </div>
        )}
      </FadeIn>
      <Pagination page={page} pageSize={pageSize} total={total} onChange={setPage} />
      <Toasts />
    </PageLayout>
  );
}
